/**
 * PhotoToolbar - Permission-aware toolbar for photo viewing/editing
 *
 * Shared component for Edit and View pages.
 * HIDE buttons based on permissions, don't disable them.
 * Pages should reflect the user's role perfectly.
 *
 * Toolbar buttons by permission:
 * - Always: Full/Cropped toggle, Crop mask toggle, Full res
 * - canEditPhotos: Crop mode, Reset crop, Confirm crop, To Editor
 * - canApprovePhotos: Mark done, Skip
 * - canDeletePhotos: Delete
 */

// ---------- Helper Components for reduced complexity ----------

/**
 * Skip confirmation toggle checkbox
 */
function SkipConfirmToggle({ skipIncompleteModal, onToggleSkipIncomplete }) {
  return (
    <label
      className="toggle-switch"
      title="Skip the incomplete faces confirmation modal when marking done"
    >
      <input
        type="checkbox"
        checked={skipIncompleteModal}
        onChange={(e) => onToggleSkipIncomplete(e.target.checked)}
      />
      <span className="toggle-slider" />
      <span className="toggle-label">Skip confirm</span>
    </label>
  );
}

/**
 * Full/Cropped segmented control
 */
function ViewModeToggle({ showVizImage, cropMode, onToggleViz }) {
  return (
    <div className={`segmented-control ${cropMode ? "disabled" : ""}`} role="tablist">
      <button
        role="tab"
        aria-selected={showVizImage}
        className={showVizImage ? "active" : ""}
        onClick={() => !cropMode && !showVizImage && onToggleViz()}
        disabled={cropMode}
        title="Show full frame with overlays (V)"
      >
        Full
      </button>
      <button
        role="tab"
        aria-selected={!showVizImage}
        className={!showVizImage ? "active" : ""}
        onClick={() => !cropMode && showVizImage && onToggleViz()}
        disabled={cropMode}
        title="Show cropped preview (V)"
      >
        Cropped
      </button>
    </div>
  );
}

/**
 * Crop mode controls (toggle, reset, confirm)
 */
function CropModeControls({
  cropMode,
  isReadOnly,
  saving,
  onToggleCropMode,
  onResetCrop,
  onConfirmCrop,
}) {
  const toggleTitle = isReadOnly
    ? "Read-only: cannot modify this photo"
    : "Toggle crop mode (S)";

  return (
    <>
      {onToggleCropMode && (
        <button
          className={`ghost ${cropMode ? "active" : ""}`}
          onClick={onToggleCropMode}
          title={toggleTitle}
          disabled={isReadOnly}
        >
          {cropMode ? "Exit crop" : "Crop mode"}
        </button>
      )}
      {cropMode && onResetCrop && (
        <button
          className="ghost"
          onClick={onResetCrop}
          title="Reset crop to original (R)"
          disabled={isReadOnly}
        >
          Reset crop
        </button>
      )}
      {cropMode && onConfirmCrop && (
        <button
          className="ghost"
          onClick={onConfirmCrop}
          disabled={saving || isReadOnly}
        >
          {saving ? "Saving..." : "Confirm crop"}
        </button>
      )}
    </>
  );
}

/**
 * View mode controls (crop mask, viz layers, full res)
 */
function ViewModeControls({
  showVizImage,
  showCropMask,
  vizLayersPreset,
  hasEditedUrl,
  onToggleCropMask,
  onCycleVizLayers,
  onFullRes,
}) {
  return (
    <>
      <button
        className="ghost"
        onClick={onToggleCropMask}
        title="Toggle crop mask (M)"
        disabled={!showVizImage}
      >
        {showCropMask ? "Crop mask on" : "Crop mask off"}
      </button>
      {onCycleVizLayers && (
        <button
          className={`ghost ${vizLayersPreset !== 'off' ? 'active' : ''}`}
          onClick={onCycleVizLayers}
          title="Cycle viz layer presets (Alt+V)"
          disabled={!showVizImage}
        >
          {vizLayersPreset === 'off' ? 'Layers' : `Layers: ${vizLayersPreset}`}
        </button>
      )}
      {onFullRes && (
        <button
          className="ghost"
          onClick={onFullRes}
          title="View full resolution image (F)"
          disabled={!hasEditedUrl}
        >
          Full res
        </button>
      )}
    </>
  );
}

/**
 * Approval action buttons (mark done, skip)
 */
function ApprovalControls({ cropMode, isReadOnly, saving, onMarkDone, onSkip }) {
  const readOnlyTitle = "Read-only: cannot modify this photo";

  return (
    <>
      {onMarkDone && (
        <button
          className="ghost"
          onClick={onMarkDone}
          disabled={saving || isReadOnly}
          title={isReadOnly ? readOnlyTitle : "Mark as done (D)"}
        >
          {saving ? "Saving..." : "Mark done"}
        </button>
      )}
      {!cropMode && onSkip && (
        <button
          className="ghost"
          onClick={onSkip}
          disabled={saving || isReadOnly}
          title={isReadOnly ? readOnlyTitle : "Skip this photo (7)"}
        >
          Skip
        </button>
      )}
    </>
  );
}

/**
 * @param {Object} props
 *
 * Photo state:
 * @param {string} props.filename - Display filename
 * @param {boolean} props.isReadOnly - Photo is read-only for current user
 * @param {boolean} props.hasEditedUrl - Whether edited URL is available
 *
 * Display state:
 * @param {boolean} props.showVizImage - Currently showing viz image
 * @param {Function} props.onToggleViz - Toggle viz/edited
 * @param {boolean} props.showCropMask - Currently showing crop mask
 * @param {Function} props.onToggleCropMask - Toggle crop mask
 *
 * Crop mode (Edit-only):
 * @param {boolean} [props.cropMode=false] - Currently in crop mode
 * @param {Function} [props.onToggleCropMode] - Toggle crop mode
 * @param {Function} [props.onResetCrop] - Reset crop to original
 * @param {Function} [props.onConfirmCrop] - Confirm crop changes
 *
 * Actions:
 * @param {Function} [props.onFullRes] - Open full resolution viewer
 * @param {Function} [props.onMarkDone] - Mark photo as done
 * @param {Function} [props.onSkip] - Skip photo
 * @param {Function} [props.onToEditor] - Send to external editor
 * @param {Function} [props.onDelete] - Delete photo
 *
 * Loading states:
 * @param {boolean} [props.saving=false] - Currently saving
 *
 * Permissions (buttons are HIDDEN if permission is false):
 * @param {boolean} [props.canEditPhotos=false] - Can edit/crop
 * @param {boolean} [props.canApprovePhotos=false] - Can mark done/skip
 * @param {boolean} [props.canDeletePhotos=false] - Can delete
 *
 * UI state:
 * @param {boolean} [props.sidebarCollapsed=false] - Sidebar collapsed state
 * @param {Function} [props.onToggleSidebar] - Toggle sidebar
 *
 * Viz layers (Edit-only):
 * @param {string} [props.vizLayersPreset='off'] - Current viz layers preset
 * @param {Function} [props.onCycleVizLayers] - Cycle to next preset
 *
 * Edit-specific options:
 * @param {boolean} [props.showSkipConfirm=false] - Show skip confirm toggle
 * @param {boolean} [props.skipIncompleteModal=false] - Skip incomplete modal preference
 * @param {Function} [props.onToggleSkipIncomplete] - Toggle skip incomplete preference
 */
export default function PhotoToolbar({
  // Photo state
  filename,
  isReadOnly = false,
  hasEditedUrl = false,
  // Display state
  showVizImage,
  onToggleViz,
  showCropMask,
  onToggleCropMask,
  // Crop mode
  cropMode = false,
  onToggleCropMode,
  onResetCrop,
  onConfirmCrop,
  // Actions
  onFullRes,
  onMarkDone,
  onSkip,
  onToEditor,
  onDelete,
  // Loading states
  saving = false,
  // Permissions
  canEditPhotos = false,
  canApprovePhotos = false,
  canDeletePhotos = false,
  // UI state
  sidebarCollapsed = false,
  onToggleSidebar,
  // Viz layers
  vizLayersPreset = 'off',
  onCycleVizLayers,
  // Edit-specific
  showSkipConfirm = false,
  skipIncompleteModal = false,
  onToggleSkipIncomplete,
}) {
  const readOnlyTitle = "Read-only: cannot modify this photo";

  return (
    <div className="row space-between">
      {/* Left side: filename and options */}
      <div className="row gap" style={{ alignItems: "center" }}>
        <h3 style={{ margin: 0 }}>{filename || "No photo selected"}</h3>

        {/* Skip confirm toggle (Edit mode only) */}
        {showSkipConfirm && onToggleSkipIncomplete && (
          <SkipConfirmToggle
            skipIncompleteModal={skipIncompleteModal}
            onToggleSkipIncomplete={onToggleSkipIncomplete}
          />
        )}
      </div>

      {/* Right side: action buttons */}
      <div className="row gap">
        {/* Full/Cropped toggle - Always visible */}
        <ViewModeToggle
          showVizImage={showVizImage}
          cropMode={cropMode}
          onToggleViz={onToggleViz}
        />

        {/* Crop mode controls - Edit permission only */}
        {canEditPhotos && (
          <CropModeControls
            cropMode={cropMode}
            isReadOnly={isReadOnly}
            saving={saving}
            onToggleCropMode={onToggleCropMode}
            onResetCrop={onResetCrop}
            onConfirmCrop={onConfirmCrop}
          />
        )}

        {/* View mode controls - When not in crop mode */}
        {!cropMode && (
          <ViewModeControls
            showVizImage={showVizImage}
            showCropMask={showCropMask}
            vizLayersPreset={vizLayersPreset}
            hasEditedUrl={hasEditedUrl}
            onToggleCropMask={onToggleCropMask}
            onCycleVizLayers={onCycleVizLayers}
            onFullRes={onFullRes}
          />
        )}

        {/* Approval controls - Approve permission only */}
        {canApprovePhotos && (
          <ApprovalControls
            cropMode={cropMode}
            isReadOnly={isReadOnly}
            saving={saving}
            onMarkDone={onMarkDone}
            onSkip={onSkip}
          />
        )}

        {/* To Editor - Edit permission only, not in crop mode */}
        {!cropMode && canEditPhotos && onToEditor && (
          <button
            className="ghost"
            onClick={onToEditor}
            disabled={isReadOnly}
            title={isReadOnly ? readOnlyTitle : "Send original to external editor"}
          >
            To Editor
          </button>
        )}

        {/* Delete - Delete permission only */}
        {canDeletePhotos && onDelete && (
          <button
            className="ghost"
            onClick={onDelete}
            disabled={isReadOnly}
          >
            Delete
          </button>
        )}

        {/* Sidebar toggle - Always visible */}
        {onToggleSidebar && (
          <button
            className="ghost toggle-btn"
            onClick={onToggleSidebar}
            title={sidebarCollapsed ? "Show sidebar" : "Hide sidebar"}
          >
            {sidebarCollapsed ? "\u25C0" : "\u25B6"}
          </button>
        )}
      </div>
    </div>
  );
}
